import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import { toast } from "/WebInterface/new-ui/modules/toast.js";

export class ImagePreview {
  constructor({ previewInfo, parent }) {
    const { href_path, mediaInfo, preview } = previewInfo;

    const source =
      preview > 0
        ? `/?command=getPreview&size=3&path=${href_path}&frame=1&c2f=${utils.cookie.get(
            "currentAuth"
          )}`
        : `/?command=download&mimeType=${
            mediaInfo.mimeType
          }&path=${href_path}&c2f=${utils.cookie.get("currentAuth")}`;

    this.loader = _$("<div>").addClass("media-loader").el();
    _$(parent).append(this.loader);

    this.image = _$("<img>").attr("src", source).el();
    _$(parent).append(this.image);

    _$(this.image).on("load", () => {
      const height = this.image.height;
      const width = this.image.width;

      const orientation = height > width ? "portrait" : "landscape";
      _$(this.image).addClass(orientation);
    });

    this.showLoader(); // Show loader immediately
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.image.addEventListener("load", () => this.hideLoader());
    this.image.addEventListener("error", () => this.handleImageError());
  }

  showLoader() {
    this.loader.style.display = "block";
  }

  hideLoader() {
    this.loader.style.display = "none";
  }

  handleImageError() {
    console.log("Image loading failed");
    this.hideLoader();
    // You can add additional error handling here if needed
  }

  resetZoom = () => {
    const { image } = this;
    const height = image.clientHeight;
    image.style.height = `auto`;
    image.style.maxHeight = `${height + 200}px`;
    image.style.maxWidth = "none";
  };

  setZoom = (zoomFactor) => {
    const { image } = this;
    const originalHeight = image.naturalHeight;
    const newHeight = originalHeight * zoomFactor;
    image.style.height = `${newHeight}px`;
    image.style.maxHeight = `${newHeight}px`;
    image.style.maxWidth = "none";
  };

  zoomIn = () => {
    const { image } = this;
    const height = image.clientHeight;
    image.style.height = `${height + 200}px`;
    image.style.maxHeight = `${height + 200}px`;
    image.style.maxWidth = "none";
  };

  zoomOut = () => {
    const { image } = this;
    const height = image.clientHeight;
    if (height - 150 < 100) image.style.height = `100px`;
    else image.style.height = `${height - 150}px`;
  };

  rotateImage = (direction) => {
    const { image } = this;

    const imageTransform = image.style.transform;
    const currentDeg = Number(
      imageTransform.substring(
        imageTransform.indexOf("(") + 1,
        imageTransform.lastIndexOf("d")
      )
    );

    image.style.transitionDuration = "0.3s";

    if (direction === "left") {
      image.style.transform = `rotate(${currentDeg - 90}deg)`;
    } else if (direction === "right") {
      image.style.transform = `rotate(${currentDeg + 90}deg)`;
    }
  };
}

export class VideoPreview {
  constructor({
    previewInfo,
    parent,
    controls = false,
    autoplay = false,
    disableContextMenu = false,
  }) {
    const { href_path, mediaInfo, preview } = previewInfo;

    const source = `/?command=download&mimeType=${
      mediaInfo?.mimeType || "video"
    }&path=${href_path}&c2f=${utils.cookie.get("currentAuth")}`;

    const coverImageSource =
      preview > 0
        ? `/?command=getPreview&size=3&path=${href_path}&frame=1&c2f=${utils.cookie.get(
            "currentAuth"
          )}`
        : ``;

    this.container = _$("<div>").addClass("video-preview-container").el();
    const video = _$("<video>").addClass("video-js").attr("preload", "auto");

    if (coverImageSource) {
      video.attr("poster", coverImageSource);
    }

    this.videoElement = video.el();

    if (disableContextMenu) {
      this.videoElement.addEventListener("contextmenu", (event) => {
        event.preventDefault();
      });
    }

    _$(this.container).append(this.videoElement);
    _$(parent).append(this.container);

    // Destroy existing player if it exists
    if (this.player) {
      this.player.dispose();
    }

    // Create new player instance
    this.player = videojs(this.videoElement, {
      controls: controls,
      autoplay: autoplay,
      preload: "auto",
      sources: [{ src: `${source}#.mp4` }],
    });

    video.on("loadedmetadata", () => {
      const height = this.videoElement.videoHeight;
      const width = this.videoElement.videoWidth;

      const orientation = height > width ? "portrait" : "landscape";
      video.addClass(orientation);
    });

    this.errorMessage = "";
    this.hasError = false;
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.player.on("loadstart", () => this.showLoader());
    this.player.on("canplay", () => this.hideLoader());
    // this.player.on("error", () => this.handleVideoError());
  }

  showLoader() {
    this.player.addClass("vjs-waiting");
  }

  hideLoader() {
    this.player.removeClass("vjs-waiting");
  }

  handleVideoError() {
    console.error("Video playback failed:", this.player.error());
    this.errorMessage = `Video playback failed: ${this.player.error().message}`;
    this.hasError = true;
    this.showErrorToast();
  }

  showErrorToast = () => {
    toast({
      type: "error",
      title: "Video Playback Error",
      message: this.errorMessage,
      autoClose: true,
      delay: 5000,
    });
  };

  // Clean up method to dispose of the Video.js player when it's no longer needed
  dispose() {
    if (this.player) {
      this.player.dispose();
    }
  }
}

export class AudioPreview {
  constructor({ previewInfo, parent }) {
    const { href_path } = previewInfo;

    this.loader = _$("<div>").addClass("media-loader").el();
    _$(parent).append(this.loader);

    this.waveContainer = _$("<div>").el();
    this.waveContainer.id = "waveform";

    this.audio = _$("<audio>").attr("src", href_path).el();
    const audio = this.audio;

    _$(parent).append(this.waveContainer);
    _$(parent).append(this.audio);

    // Function to get computed CSS variable value
    function getCSSVariableValue(variableName) {
      return getComputedStyle(document.documentElement)
        .getPropertyValue(variableName)
        .trim();
    }

    // Get color values
    const progressColor = getCSSVariableValue("--accent-color");
    const waveColor = getCSSVariableValue("--border-color");
    const cursorColor = getCSSVariableValue("--border-color");

    const waveSurfer = WaveSurfer.create({
      container: "#waveform",
      barWidth: 1,
      barGap: 1,
      height: 100,
      barRadius: 2,
      progressColor: progressColor,
      waveColor: waveColor,
      cursorColor: cursorColor,
      backend: "MediaElement",
      hideScrollbar: true,
      fillParent: true,
      responsive: true,
    });

    waveSurfer.load(audio);

    this.showLoader(); // Show loader immediately
    this.setupEventListeners();
    this.setupWaveSurfer(waveSurfer);
  }

  setupEventListeners() {
    this.audio.addEventListener("loadstart", () => this.showLoader());
    this.audio.addEventListener("canplay", () => this.hideLoader());
    this.audio.addEventListener("error", () => this.handleAudioError());
  }

  showLoader() {
    this.loader.style.display = "block";
  }

  hideLoader() {
    this.loader.style.display = "none";
  }

  handleAudioError() {
    console.log("Audio loading failed");
    this.hideLoader();
  }

  setupWaveSurfer(waveSurfer) {
    waveSurfer.on("ready", () => this.hideLoader());
  }
}
